<template>
  <div class="m-combo-market-list">
    <template v-if="!loading">
      <BetBuilderTip v-if="!isEmpty" />
      <PopularCombos
        v-if="isPrematch && comboMarket && comboMarket.popularCombos.length && show"
        :combos="comboMarket.popularCombos"
        :event="event"
        :toggle="toggle"
        :selection-list="selectionList"
      />
      <div class="m-market-list">
        <template v-if="show && !isEmpty">
          <div
            v-for="(group, index) in comboMarket.comboMarketGroups"
            ref="marketItemRef"
            :key="index"
            class="m-combo-market-item-wrapper"
          >
            <MarketComboItem
              v-if="group.comboType !== -1 && group.marketGroups.length"
              :key="`${group.comboGroupName}_${index}`"
              :group="group"
              :should-expand="shouldMarketExpand(group.comboGroupName)"
              @toggle-expand="() => handleToggleExpand({ marketId: group.comboGroupName })"
              @active-change="({ active }) => handleMarketItemActiveChange({ marketId: group.comboGroupName, active })"
            />
            <template v-else>
              <template v-for="marketGroup in group.marketGroups">
                <template v-for="market in getMarkets(marketGroup.markets)">
                  <MarketItem
                    :key="market"
                    :market-id="market"
                    :market="comboMarketGroups.map[market]"
                    :should-expand="shouldMarketExpand(group.comboGroupName)"
                    @toggle-expand="() => handleToggleExpand({ marketId: group.comboGroupName })"
                    @active-change="({ active }) => handleMarketItemActiveChange({ marketId: group.comboGroupName, active })"
                  />
                </template>
              </template>
            </template>
          </div>
        </template>

        <template v-else>
          <NoMarkets />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, watch, onMounted, onUnmounted, onActivated, onDeactivated, ref, nextTick } from '@vue/composition-api'
import Vue from 'vue'
import { storeToRefs } from 'pinia'
import { xorBy } from 'lodash-es'
import { useDetailStore } from '@ms/stores/matchDetail'
import { useMatchDetailStore } from 'wap/stores/matchDetail'
import { useWapBetslipStore } from 'wap/stores/betslip'
import { getMarketSymbolId } from '@ms/components/match/update-details.js'
import useMatchInfo from '@ms/components/match/useMatchInfo.js'
import PopularCombos from '@ms/components/matchDetail/PopularCombos.vue'
import MarketItem from './MarketItem'
import MarketComboItem from './MarketComboItem'
import BetBuilderTip from '@ms/components/matchDetail/BetBuildTip.vue'
import NoMarkets from './NoMarkets'
import { useExpandedMarkets } from 'composables/useExpandedMarkets.js'
import eventBus from '@ms/utils/event-bus.js'

export default {
  name: 'MarketComboList',
  components: {
    PopularCombos,
    MarketItem,
    MarketComboItem,
    NoMarkets,
    BetBuilderTip
  },
  props: {
    // 列表 group name
    name: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: true
    }
  },
  setup() {
    const isActivated = ref(false)
    const lastScrollY = ref(null)

    const { isPrematch } = useMatchInfo()

    const marketItemRef = ref([])
    const firstVisibleMarketItemIndex = ref(null)
    const stickedHeaderHeight = ref(0)

    const matchDetailStore = useMatchDetailStore()

    const { event } = storeToRefs(matchDetailStore)

    const { comboMarket, comboMarketGroups } = storeToRefs(useDetailStore())

    const { selectionList } = storeToRefs(useWapBetslipStore())

    const isEmpty = computed(() => {
      if (!comboMarket.value) return true
      const { popularCombos, comboMarketGroups } = comboMarket.value
      const hasPopular = popularCombos && popularCombos.length
      const hasCombos = comboMarketGroups && comboMarketGroups.length
      return !hasPopular && !hasCombos
    })

    const curBetBuilderSelections = computed(() => {
      const betBuilderId = `${event.value.eventId}_betBuilder`
      const curBetBuilder = selectionList.value.filter((x) => x.id === betBuilderId)

      return curBetBuilder.length ? [...curBetBuilder[0].betBuilderSelections] : []
    })

    watch(curBetBuilderSelections, (val, oldVal) => {
      if (val.length < oldVal.length) {
        const markets = xorBy(val, oldVal, 'id')
        const selections = markets.map((selection) => {
          return {
            outcome: selection.outcomeInfo,
            market: selection.marketInfo
          }
        })

        if (!selections.length) return false
        useDetailStore().getConflictMarkets(selections, true, event.value.eventId)
      }
    })

    onActivated(() => {
      isActivated.value = true
      restoreScrollY()

      // 解决因keep-alive导致的出现重复v-block-loading，因此在activeed时先清除原有node
      const blockLoadingNode = document.querySelector('.m-combo-market-list')
      const childNode = blockLoadingNode.querySelectorAll('.m-block-loading')

      if (childNode && childNode.length > 1) {
        blockLoadingNode.removeChild(childNode[0])
      }
    })

    onDeactivated(() => {
      isActivated.value = false
    })

    const { expandedMarketGroupMap, toggleMarketExpand, collapseAllMarkets, expandAllMarkets, setMarketActive, setMarketInactive } = useExpandedMarkets()
    const expandedMarketIdList = computed(() => expandedMarketGroupMap.value['Bet Builder'] ?? [])
    const shouldMarketExpand = (targetMarketId) => {
      return expandedMarketIdList.value.some((marketId) => {
        return marketId === targetMarketId
      })
    }

    const handleToggleExpand = (options = {}) => {
      const { marketId } = options

      toggleMarketExpand({
        marketGroup: 'Bet Builder',
        marketId
      })
    }

    const handleMarketItemActiveChange = (options = {}) => {
      const { marketId, active } = options

      if (active) {
        setMarketActive(marketId)
        return
      }

      setMarketInactive(marketId)
    }

    const getStickedHeaderHeight = () => {
      const pageHeaderHeight = document.querySelector('.m-detail-top-header')?.clientHeight ?? 0
      const teamsInfoHeight = document.querySelector('.m-live-summary .teams-info')?.clientHeight ?? 0
      const marketGroupNavHeight = document.querySelector('.m-detail-markets .m-sub-navs-wrapper')?.clientHeight ?? 0

      return pageHeaderHeight + teamsInfoHeight + marketGroupNavHeight
    }

    const isHeaderSticked = () => {
      const pageHeaderHeight = document.querySelector('.m-detail-top-header')?.clientHeight ?? 0
      const teamsInfoHeight = document.querySelector('.m-live-summary .teams-info')?.clientHeight ?? 0
      const marketGroupNavEl = document.querySelector('.m-detail-markets .m-sub-navs-wrapper')

      if (marketGroupNavEl == null) {
        return false
      }
      return marketGroupNavEl.getBoundingClientRect().top === pageHeaderHeight + teamsInfoHeight
    }

    const setFirstVisibleMarket = () => {
      if (marketItemRef.value == null || marketItemRef.value.length === 0) {
        return
      }

      const marketItemIndex = marketItemRef.value.findIndex((item) => {
        const el = item instanceof Vue ? item.$el : item

        if (el.getBoundingClientRect == null) {
          return false
        }

        const marketItemHeaderHeight = el.querySelector('.m-market-item--header')?.getBoundingClientRect()?.height ?? 0
        const visibleTopLimit = stickedHeaderHeight.value + marketItemHeaderHeight / 2

        return el.getBoundingClientRect().top <= visibleTopLimit && el.getBoundingClientRect().bottom > visibleTopLimit
      })

      if (marketItemIndex === -1) {
        firstVisibleMarketItemIndex.value = null
        return
      }

      firstVisibleMarketItemIndex.value = marketItemIndex
    }

    /**
     * Get the first visible market element. If the element is not found, get the last element
     * @returns {HTMLElement | null}
     */
    const getMarketItemScrollElement = () => {
      const refItem = marketItemRef.value[firstVisibleMarketItemIndex.value]

      if (refItem != null) {
        return refItem instanceof Vue ? refItem.$el : refItem
      }

      const lastRefItem = marketItemRef.value[marketItemRef.value.length - 1]
      return lastRefItem instanceof Vue ? lastRefItem.$el : lastRefItem
    }

    /**
     * @param {number | null} scrollYBeforeExpand
     */
    const scrollToFirstVisibleMarket = (scrollYBeforeExpand) => {
      if (marketItemRef.value == null || marketItemRef.value.length === 0 || firstVisibleMarketItemIndex.value == null) {
        // prevent chrome's scroll bar sticked at the bottom
        scrollYBeforeExpand != null && scrollTo(0, scrollYBeforeExpand)
        return
      }

      const el = getMarketItemScrollElement()
      const scrollY = el.getBoundingClientRect().top + window.scrollY - stickedHeaderHeight.value
      if (!el) return
      scrollTo(0, scrollY)
    }

    const getFirstMarketScrollY = () => {
      if (marketItemRef.value == null || marketItemRef.value.length === 0) {
        return
      }

      const el = marketItemRef.value[0]
      if (!el) return 0
      return el.getBoundingClientRect().top + window.scrollY - stickedHeaderHeight.value
    }

    const scrollToFirstMarket = () => {
      const firstMarketScrollY = getFirstMarketScrollY()
      scrollTo(0, firstMarketScrollY)
    }

    const handleCollapseTogglerClick = async (options) => {
      if (!isActivated.value) {
        return
      }

      const { shouldCollapse } = options

      setFirstVisibleMarket()

      if (shouldCollapse) {
        collapseAllMarkets({ marketGroup: 'Bet Builder' })
        nextTick(() => {
          scrollToFirstVisibleMarket()
        })
        return
      }

      const scrollYBeforeExpand = window.scrollY

      expandAllMarkets({ marketGroup: 'Bet Builder' })
      nextTick(() => {
        scrollToFirstVisibleMarket(scrollYBeforeExpand)
      })
    }

    const updateScrollY = () => {
      if (!isActivated.value) {
        return
      }

      lastScrollY.value = window.scrollY
    }

    const restoreScrollY = () => {
      if (!isActivated.value) {
        return
      }

      if (isHeaderSticked()) {
        if (firstVisibleMarketItemIndex.value != null) {
          scrollToFirstVisibleMarket()
          return
        }

        if (lastScrollY.value == null || lastScrollY.value < getFirstMarketScrollY()) {
          scrollToFirstMarket()
          return
        }

        scrollTo(0, lastScrollY.value)
      }

      firstVisibleMarketItemIndex.value = null
      updateScrollY()
    }

    onMounted(() => {
      eventBus.$on('collapse-toggler-click', handleCollapseTogglerClick)
      eventBus.$on('mini-footer-visible', restoreScrollY)
      stickedHeaderHeight.value = getStickedHeaderHeight()
      window.addEventListener('scroll', updateScrollY)
    })

    onUnmounted(() => {
      eventBus.$off('collapse-toggler-click', handleCollapseTogglerClick)
      eventBus.$off('mini-footer-visible', restoreScrollY)
      window.removeEventListener('scroll', updateScrollY)
    })

    return {
      event,
      isPrematch,
      comboMarket,
      comboMarketGroups,
      selectionList,
      isEmpty,
      toggle: useWapBetslipStore().toggleSelection,
      expandedMarketIdList,
      handleToggleExpand,
      shouldMarketExpand,
      expandedMarketGroupMap,
      marketItemRef,
      firstVisibleMarketItemIndex,
      handleMarketItemActiveChange
    }
  },
  data() {
    return {
      show: true
    }
  },
  computed: {
    hasData() {
      const { map, order } = this.comboMarketGroups
      // 有可投的 market
      if (order && order.length) {
        return order.some((id) => {
          const market = map[id]
          if (Array.isArray(market)) {
            return market.some((x) => x.status === 0 || x.status === 1)
          } else {
            return market.status === 0 || market.status === 1
          }
        })
      }

      return false
    }
  },
  watch: {
    hasData(val) {
      clearTimeout(this._timer)
      if (!val) {
        this._timer = setTimeout(() => {
          this.show = val
        }, 5000)
      } else {
        this.show = true
      }
    },
    active: {
      immediate: true,
      handler(currActive) {
        this.$emit('active-change', {
          marketId: this.marketId,
          active: currActive
        })
      }
    }
  },
  mounted() {
    if (!this.hasData) {
      this.show = false
    }
  },
  methods: {
    getMarkets(markets) {
      const ids = []

      markets.forEach((x) => {
        const key = getMarketSymbolId(x)
        if (!ids.includes(key)) ids.push(key)
      })
      return ids
    }
  }
}
</script>

<style lang="less">
@import url('~wap/common/style/variable.less');
@import url('../style/market.less');

.m-market-list {
  padding: 0 6px;

  &.favorite-guide {
    padding: 0 5 / @rem;
  }

  // .m-market-item {
  //   &:last-child {
  //     border-bottom: 1px solid #323232;
  //   }
  // }

  .img-container {
    margin-top: 25 / @rem;
    text-align: center;

    img {
      max-width: 100%;
      vertical-align: top;
    }
  }

  .m-combo-market-item-wrapper {
    border-radius: 12px;
    margin-top: 6px;
    background: #161616;
  }
}
</style>

<style lang="scss" scoped>
[data-mode="light"] .m-market-list {
  .m-combo-market-item-wrapper {
    background: #fff;
  }
}
</style>
